/**
 * Where a `position: fixed` popover goes under its button — right edges lined
 * up, pulled back inside the window.
 *
 * Fixed rather than absolute for `FieldRef`'s reason: the Conditions tab's
 * editor scrolls (`overflow: auto`), and an absolutely positioned popover
 * anchored `right: 0` to a button in its middle hung off the pane's left edge
 * and was clipped there — Link from screen… opened as an empty box, its screen
 * names cut away (owner, 2026-10-19).
 */

import { useLayoutEffect, useState, type RefObject } from "react";

export function usePopoverPlace(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  width: number,
  /** How tall it may get — less room than this below, and it opens upward. */
  tallest = 420,
): React.CSSProperties | null {
  const [at, setAt] = useState<React.CSSProperties | null>(null);
  useLayoutEffect(() => {
    if (!open) {
      setAt(null);
      return;
    }
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const w = Math.min(width, window.innerWidth - 8);
    const left = Math.min(Math.max(4, rect.right - w), window.innerWidth - w - 4);
    const below = window.innerHeight - rect.bottom - 8;
    const above = rect.top - 8;
    // Near the bottom of the window it opens upward, grown from the button's
    // top, rather than running off the screen (owner, 2026-10-19).
    const up = below < tallest && above > below;
    setAt({
      position: "fixed",
      ...(up
        ? { top: "auto", bottom: window.innerHeight - rect.top + 4, maxHeight: above }
        : { top: rect.bottom + 4, bottom: "auto", maxHeight: below }),
      left,
      right: "auto",
      width: w,
      overflowY: "auto",
      marginTop: 0,
    });
  }, [open, anchorRef, width, tallest]);
  return at;
}
